import { NavButton } from 'Components/ClientApp/CombinedApp/Navigation/NavButton';
import React from 'react';
import { clientPaths } from 'Consts/Strings';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import moment from 'moment/moment';
import { fullDateTimeNoSeconds } from 'Consts/DateFormats';
import useCollectionState from 'Hooks/useCollectionState';
import { pick } from 'Helpers/Affordances';
import useHomeContext from 'Context/HomeContext';
import { encodeQuery } from 'Helpers/Query';
import useMobileLayout from 'Hooks/useMobileLayout';
import { buildClientLink, buildPageLink } from 'Views/Page/Content/Templates';

export const PlanningNavButton = () => {
  const { householdResource } = useSimpleHousehold();
  const { homeResource } = useHomeContext();
  const { collection: nextMeetingCol } = useCollectionState({ link: householdResource?.relationship(fr.relationships.nextMeeting.name)?.link('related') });
  const isMobile = useMobileLayout();

  const myPlannerLink = householdResource.withRelationship(
    fr.relationships.myPlanner.name,
    (rel) => rel.link('related').url(),
    () => undefined,
  );
  const myPlannerPrompt = householdResource.withRelationship(
    fr.relationships.myPlanner.name,
    (rel) => rel.meta('prompt'),
    () => undefined,
  );
  const { collection: myPlannerCollection } = useCollectionState({ link: myPlannerLink });
  const myPlanner = myPlannerCollection?.resources()?.head();

  let myMeetingsSecondaryText = 'See your past and upcoming meetings.';
  if (nextMeetingCol?.resources()?.head()) {
    myMeetingsSecondaryText = `Next meeting - ${moment(nextMeetingCol.resources().head().attr('startTime')).format(fullDateTimeNoSeconds)}`;
  }

  const goalsPage = householdResource.withRelationship(fr.relationships.goalsPage.name,
    (rel) => ({
      key: 'goals',
      primaryText: 'Goals',
      secondaryText: 'Important financial milestones.',
      to: buildClientLink(rel.link('related')),
      excludesMobile: true,
    }),
    () => null);

  const legacyGoals = goalsPage ? null : householdResource.withRelationship(fr.relationships.goals.name,
    (_) => ({
      key: 'goals',
      primaryText: 'Goals',
      secondaryText: 'Important financial milestones.',
      to: clientPaths.goals,
      excludesMobile: true,
    }),
    () => null);

  const scenarioOrGoals = pick(
    homeResource,
    {
      [fr.relationships.roadmapDeliverable.name]: {
        key: 'scenarios',
        primaryText: 'Roadmap',
        secondaryText: 'Your roadmap to your financial goals.',
        to: `${clientPaths.scenarios}${encodeQuery({
          pageSelfLink: homeResource.relationship(fr.relationships.roadmapDeliverable.name)?.link('related').url(),
        })}`,
      },
      [fr.relationships.scenarioReview.name]: {
        key: 'scenarios',
        primaryText: 'Roadmap',
        secondaryText: 'Your roadmap to your financial goals.',
        to: `${clientPaths.scenarios}${encodeQuery({
          pageSelfLink: homeResource.relationship(fr.relationships.scenarioReview.name)?.link('related').url(),
        })}`,
      },
      [fr.relationships.facetPlanClientView.name]: {
        key: 'scenarios',
        primaryText: 'Roadmap',
        secondaryText: 'Your roadmap to your financial goals.',
        to: `${clientPaths.scenarios}${encodeQuery({
          pageSelfLink: homeResource.relationship(fr.relationships.facetPlanClientView.name)?.link('related').url(),
        })}`,
      },
    },
    legacyGoals,
  );

  const dropdownItems = [
    goalsPage,
    scenarioOrGoals,
    householdResource.withRelationship(fr.relationships.householdMeetings.name,
      (_) => {
        return {
          key: 'meetings', primaryText: 'Meetings', secondaryText: myMeetingsSecondaryText, to: clientPaths.myMeetings,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.files.name,
      (_) => {
        return {
          key: 'files', primaryText: 'Files', secondaryText: 'View and upload files. (Secure and encrypted.)', to: clientPaths.files,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.blueprint.name,
      (_) => {
        return {
          key: 'blueprint', primaryText: 'Blueprint', secondaryText: 'Your financial info all in one place.', to: clientPaths.blueprint,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.questionnaire.name,
      (_) => {
        const secondaryText = householdResource.withRelationship(fr.relationships.goalsPage.name,
          () => 'View and update your financial info.',
          () => 'View and update your goals and financial info.');
        return {
          key: 'questionnaire',
          primaryText: 'Questionnaire',
          secondaryText,
          to: clientPaths.questionnaire,
          excludesMobile: true,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.cashFlowPage.name,
      (rel) => {
        return {
          key: 'cashFlowPage',
          primaryText: 'Cash Flow',
          secondaryText: 'An overview of your income and expenses.',
          to: buildPageLink(rel.link('related'), clientPaths.cashFlowPage),
        };
      },
      () => null),
    pick(householdResource, {
      [fr.relationships.householdBalanceSheetPage.name]() {
        return {
          key: 'balanceSheet',
          primaryText: 'Balance Sheet & Accounts',
          secondaryText: 'An overview of your assets and liabilities.',
          to: buildPageLink(householdResource.relationship(fr.relationships.householdBalanceSheetPage.name).link('related'), clientPaths.balanceSheet),
        };
      },
    }, () => undefined)(),
    householdResource.withRelationship(fr.relationships.estatePlanningPage.name,
      () => {
        return {
          key: 'estate-planning',
          primaryText: 'Estate Planning',
          secondaryText: 'Ensure you have an estate plan in place.',
          to: `${clientPaths.page}${encodeQuery({
            pageSelfLink: householdResource.relationship(fr.relationships.estatePlanningPage.name)?.link('related').url(),
          })}`,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.taxHomePage.name,
      (rel) => {
        return {
          key: 'tax-planning',
          primaryText: rel.meta('prompt'),
          secondaryText: rel.meta('description'),
          to: `${clientPaths.page}${encodeQuery({
            pageSelfLink: householdResource.relationship(fr.relationships.taxHomePage.name)?.link('related').url(),
          })}`,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.desiredOutcomesSummaryPage.name,
      (_) => {
        return {
          key: 'outcomes',
          primaryText: 'Outcomes',
          secondaryText: 'Important financial milestones.',
          to: `${clientPaths.outcomes}${encodeQuery({
            pageSelfLink: householdResource.relationship(fr.relationships.desiredOutcomesSummaryPage.name)?.link('related').url(),
          })}`,
          excludesMobile: true,
        };
      },
      () => null),
    householdResource.withRelationship(fr.relationships.taxDiagnosticMemberWizard.name,
      (rel) => {
        return {
          key: 'tax-diagnostic',
          primaryText: 'Tax Diagnostic',
          secondaryText: 'Tax-saving opportunities personalized for you.',
          to: buildPageLink(
            rel.link('related'),
            clientPaths.page,
            { householdId: householdResource.attr('householdId') },
          ),
        };
      },
      () => null),
    myPlanner && {
      key: 'myPlanner',
      primaryText: myPlanner.attr('name'),
      secondaryText: myPlannerPrompt,
      avatar: myPlanner.attr('profileURL'),
    },
  ];

  return (
    <NavButton
      value="planning"
      label="Planning"
      isDropdown
      dropdownItems={dropdownItems
        .filter(Boolean)
        .filter((item) => (isMobile ? !item?.excludesMobile : item))
        .filter((item) => item.primaryText) // if an item is missing primary text (due to missing meta) filter it out to prevent an unplug
        .sort((a, b) => {
          // Always sort myPlanner to the top
          if (a.key === 'myPlanner') {
            return -1;
          }
          if (b.key === 'myPlanner') {
            return 1;
          }
          // The rest can be sorted alphabetically
          return a.primaryText.localeCompare(b.primaryText);
        })
      }
    />
  );
};
